import React, { FunctionComponent, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { SurveyStatus } from '../../common/enums/SurveyStatus';
import { IBindingAction, IBindingCallback1 } from '../../common/models/callback';
import { IReferencesStatus } from '../../common/models/reference/IReferenceStatus';
import { IReferenceSurvey } from '../../common/models/reference/IReferenceSurvey';
import { IAppState } from '../../common/models/store/IAppState';
import { IUser } from '../../common/models/user/IUser';
import Icon from '../../components/Icon';
import LoaderWrapper from '../../components/LoaderWrapper';
import RegularButton from '../../components/RegularButton';
import { updateRefStatusRoutine } from '../../scenes/ContactProfile/routines';
import Survey from '../../scenes/SurveyUI/containers/Survey';
import { startSurveyRoutine } from '../../scenes/SurveyUI/routines';
import { getRefForManualInputRoutine, resetRefSurveyDataRoutine } from './routines';
import styles from './styles.module.scss';

interface IProps extends RouteComponentProps {
  user: IUser;
  reference: IReferenceSurvey;
  isManualInput: boolean;
  surveyStartDate?: Date;
  startSurvey: IBindingCallback1<{ id: string; isManualInput: boolean }>;
  getRefForManualInput: IBindingCallback1<string>;
  updateRefStatus: IBindingCallback1<IReferencesStatus>;
  resetSurveyData: IBindingAction;
}

const SurveyManualInput: FunctionComponent<IProps> = ({
  history,
  match,
  reference,
  isManualInput,
  surveyStartDate,
  startSurvey,
  getRefForManualInput,
  updateRefStatus,
  resetSurveyData
}) => {
  const [showSurvey, setShowSurvey] = useState(false);
  const id = (match?.params as Record<string, string>)?.id;

  useEffect(() => {
    getRefForManualInput(id);
    return () => resetSurveyData();
  }, []);

  useEffect(() => {
    if (surveyStartDate) {
      setShowSurvey(true);
    }
  }, [surveyStartDate]);

  const sendSurvey = () => {
    updateRefStatus({ ids: [reference.id], status: SurveyStatus.Requested });
    history.goBack();
  };

  const onStart = () => {
    startSurvey({ id: reference.id, isManualInput: true });
    setShowSurvey(true);
  };

  return (
    <LoaderWrapper loading={!reference?.id || !isManualInput}>
      {showSurvey
        ? <Survey />
        : (
          <div className={styles.manualInputPage}>
            <h1 className={styles.header}>
              <FormattedMessage id="manualInput" defaultMessage="Manual input" />
            </h1>
            <p className={styles.body}>
              <FormattedMessage
                id="manualInput.description"
                defaultMessage="Enter {firstName} {lastName}’s answers to these questions."
                values={{ firstName: reference?.firstName, lastName: reference?.lastName }}
              />
            </p>
            <div className={styles.buttons}>
              <RegularButton
                textIntlId="manualInput.sendSurvey"
                defaultMessage="Send survey instead"
                variant="secondary"
                onClick={sendSurvey}
              />
              <RegularButton
                textIntlId="candidateForm.getStarted"
                defaultMessage="Get started"
                icon={<Icon icon="arrowRight" />}
                onClick={onStart}
                iconPosition="right"
              />
            </div>
          </div>
        )}
    </LoaderWrapper>
  );
};

const mapStateToProps = ({ refSurvey: { isManualInput, reference, surveyStartDate } }: IAppState) => (
  { isManualInput, reference, surveyStartDate }
);

const mapDispatchToProps = {
  getRefForManualInput: getRefForManualInputRoutine,
  updateRefStatus: updateRefStatusRoutine,
  resetSurveyData: resetRefSurveyDataRoutine,
  startSurvey: startSurveyRoutine
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(SurveyManualInput));
